/* assets/race-finder.js — Race Finder for Medal Monday Club.
 * Vanilla JS, no build step. Created 12 July 2026.
 *
 * Loads a compact JSON index of all filterable races through the Section
 * Rendering API (sections/race-finder-data.liquid), one collection at a time:
 *   /collections/<handle>?section_id=race-finder-data&page=<n>
 * Records are merged by product handle across collections; tag/metafield data
 * always wins, collection-block fallbacks only fill missing fields.
 *
 * Index loading, text helpers and the autocomplete mechanics live in
 * assets/race-search-core.js (shared with the Race Search section) — that
 * script must be loaded first; the section emits both tags in order.
 *
 * All customer-facing strings and label maps come from the section's JSON
 * config blob (they live in Liquid, not here). See README.md.
 */
(function () {
  'use strict';

  // The theme editor re-executes section-embedded scripts on every section
  // re-render; a second execution would stack document listeners and
  // double-initialize the section (duplicate fetches, dead map).
  if (window.__mmcRaceFinderLoaded) return;
  window.__mmcRaceFinderLoaded = true;

  if (!window.MMCRaceSearch) {
    // eslint-disable-next-line no-console
    console.error('[race-finder] race-search-core.js must be loaded first');
    return;
  }
  const {
    normalizeText,
    raceBaseTitle,
    raceGroupKey,
    variantLabel,
    variantOrder,
    TYPE_LABELS,
    matchesQuery,
    fmt,
    fallbackAcItems,
    sessionGet,
    sessionSet,
    parseCoord,
    sortCollections,
    IndexLoader,
    Autocomplete,
    createLogoAlign,
  } = window.MMCRaceSearch;

  const GRID_PAGE_SIZE = 24;
  // "distance" is not a facet of its own: distance keys are merged into the
  // race type dropdown (values encoded as "dist:<key>").
  const FACETS = ['racetype', 'region', 'country'];
  // Canonical display order for known distance keys; unknown keys follow alphabetically.
  const DISTANCE_ORDER = ['marathon', 'half', 'ultra', '70.3', '140.6'];

  const instances = new Map();
  const loadedAssets = new Map();

  // White abbreviation inside the pin — distance first, then race type.
  // Trademark-safe: no "Ironman"/"IM" anywhere customer-facing.
  const DIST_ABBREV = { marathon: 'M', half: 'HM', ultra: 'U', '70.3': '70.3', '140.6': '140.6' };
  const TYPE_ABBREV = { ironman: 'TRI' };
  function pinAbbrev(record) {
    if (record.d) {
      // Unknown distance keys show verbatim (e.g. "10k"), capped at 4 chars.
      return DIST_ABBREV[record.d] || record.d.slice(0, 4);
    }
    if (record.ty) return TYPE_ABBREV[record.ty] || record.ty.slice(0, 2).toUpperCase();
    return '';
  }

  function loadScript(src) {
    if (loadedAssets.has(src)) return loadedAssets.get(src);
    const promise = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = src;
      el.onload = resolve;
      el.onerror = () => {
        el.remove();
        reject(new Error('Failed to load ' + src));
      };
      document.head.appendChild(el);
    });
    // Never memoize failures — a later attemptMapInit retry must re-fetch.
    promise.catch(() => loadedAssets.delete(src));
    loadedAssets.set(src, promise);
    return promise;
  }

  function loadStylesheet(href) {
    if (loadedAssets.has(href)) return loadedAssets.get(href);
    const promise = new Promise((resolve, reject) => {
      const el = document.createElement('link');
      el.rel = 'stylesheet';
      el.href = href;
      el.onload = resolve;
      el.onerror = () => {
        el.remove();
        reject(new Error('Failed to load ' + href));
      };
      document.head.appendChild(el);
    });
    promise.catch(() => loadedAssets.delete(href));
    loadedAssets.set(href, promise);
    return promise;
  }

  /* ---------- dotted halftone map style ----------
     No basemap at all: a background layer in the section's own background
     color plus dotted land in a two-technique HYBRID (everything from the
     shop's CDN — no third-party requests, hence no consent gate):
     - Zoom < 4 (world/continent): a PRECOMPUTED dot grid (MultiPoint,
       derived from Natural Earth 110m land, cell centers on land only) as
       a circle layer — every dot draws whole, coastlines look clean, and
       the dots scale with the map like a magnified poster.
     - Zoom >= 4 (detail): the land polygons (Natural Earth 50m) with a
       canvas fill-pattern — CONSTANT screen density (8px cells, 0.9px
       dots) on every level, the look Philipp preferred. Half dots at
       coastline clips are the accepted trade-off in detail views.
     Dot color comes from the section setting (mapDotColor). */
  const MAP_DOT_COLORS = { light: '#1d1d1d', dark: '#2e2e2e' };
  const MAP_BG_FALLBACK = { light: '#ffffff', dark: '#141414' };
  /* Grid bands below zoom 4: one grid resolution per zoom band, so the
     screen density stays in the 8-16px range everywhere (like the
     fill-pattern, which re-rasterizes per level). Within a band the dots
     scale with the map (exponential base 2 = the spacing growth); at each
     band switch the density resets. Radii keep the halftone ratio (~0.9px
     dot per 8px spacing). */
  /* Band switches sit HALF a level earlier than the pure 8-16px window,
     so the default zoom-out floor (1.5) already shows the next finer grid
     — spacing stays in the ~6.5-13px range on every level (denser look,
     Philipp's preference at the overview). */
  const MAP_DOT_BANDS = [
    { key: 'dotsWorld', id: 'rf-land-dots-world', minzoom: 0, maxzoom: 1.5,
      radius: ['interpolate', ['exponential', 2], ['zoom'], 0, 0.5, 1.5, 1.45] },
    { key: 'dotsCoarse', id: 'rf-land-dots-coarse', minzoom: 1.5, maxzoom: 2.5,
      radius: ['interpolate', ['exponential', 2], ['zoom'], 1.5, 0.72, 2.5, 1.45] },
    { key: 'dotsMid', id: 'rf-land-dots-mid', minzoom: 2.5, maxzoom: 3.5,
      radius: ['interpolate', ['exponential', 2], ['zoom'], 2.5, 0.72, 3.5, 1.45] },
  ];

  /* The style itself is minimal — the land sources/layers join in the
     map's 'load' handler (like the races layers), after the dot pattern
     image is registered. Deliberately NO glyphs endpoint: the style has no
     text layers at all — even the cluster counts render as canvas-drawn
     IMAGES (see addCountImage), because font PBFs cannot be hosted as
     Shopify theme assets and a failed glyph fetch makes MapLibre drop the
     WHOLE tile (clusters included). */
  function dottedMapStyle(bg) {
    return {
      version: 8,
      sources: {},
      layers: [
        { id: 'rf-bg', type: 'background', paint: { 'background-color': bg } },
      ],
    };
  }

  /* Dot sprite for the detail fill-pattern: one cell of the halftone grid,
     drawn at devicePixelRatio so the dots stay crisp on retina. The pattern
     is anchored to tile space — it pans with the map, scales smoothly
     during the zoom gesture and re-rasterizes per level, which keeps the
     screen density constant. */
  function makeDotPattern(color) {
    const CELL = 8; // CSS px per pattern cell
    const RADIUS = 0.9;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const size = Math.round(CELL * dpr);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, RADIUS * dpr, 0, Math.PI * 2);
    ctx.fill();
    return { image: ctx.getImageData(0, 0, size, size), pixelRatio: dpr };
  }

  class RaceFinder {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-rf-config]').textContent);
      this.strings = this.config.strings || {};
      this.labels = this.config.labels || { distance: {}, region: {} };
      // sortCollections: metaobject-sourced lists carry an explicit `sort`
      // (Liquid emits metaobjects in no guaranteed order); block-sourced
      // lists arrive pre-sorted with sort = block index.
      this.collections = sortCollections(
        (this.config.collections || [])
          .filter((c) => c.handle)
          .map((c) =>
            Object.assign({}, c, {
              lat: parseCoord(c.lat, 90),
              lng: parseCoord(c.lng, 180),
            })
          )
      );
      this.collectionByHandle = new Map(this.collections.map((c) => [c.handle, c]));
      this.numberFormat = new Intl.NumberFormat('en-US');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Index loading (records/cache/failed collections) lives in the shared
      // IndexLoader; this.records aliases its Map — same object, mutated by
      // the loader as pages arrive.
      this.loader = new IndexLoader({
        collections: this.collections,
        dataVersion: this.config.dataVersion,
        onUpdate: () => this.scheduleRefresh(),
        onNewRecord: () => {
          this.sortDirty = true;
        },
      });
      this.records = this.loader.records;
      this.cardCache = new Map(); // handle -> mounted grid <li> (avoids image flicker on re-render)
      this.themeCards = new Map(); // handle -> theme product-card <li> fetched from race-finder-cards
      this.cardsStarted = false;
      this.cardsIo = null;
      this.sortedRecords = [];
      this.sortDirty = false;
      this.matches = [];

      this.state = { q: '', racetype: null, region: null, country: null };
      this.gridPages = 1;
      this.renderedCount = 0;

      this.inView = false;
      this.map = null;
      this.mapReady = false;
      this.mapGroups = new Map(); // group key -> records bundled into one pin
      this.pendingPins = new Set(); // pin-image ids currently rasterizing
      this.sheetRecords = [];
      this.sheetActiveIndex = 0;
      this.sheetReturnFocus = null;
      this.mapLoading = false;
      this.userMovedMap = false; // visitor panned/zoomed — no auto re-fit
      this.mapFitFinal = false; // the complete-index fitToMatches happened
      this.refreshQueued = false;

      // Map/grid view: deep link (?rf_view=grid|map) wins, else remembered
      // per tab, else the section's default.
      const urlView = /[?&]rf_view=(grid|map)(?:&|$)/.exec(window.location.search);
      const storedView = sessionGet('rf:view');
      this.view = urlView
        ? urlView[1]
        : storedView === 'map' || storedView === 'grid'
          ? storedView
          : this.config.defaultView === 'grid'
            ? 'grid'
            : 'map';

      this.els = {
        q: root.querySelector('[data-rf-q]'),
        ac: root.querySelector('[data-rf-ac]'),
        clear: root.querySelector('[data-rf-clear]'),
        form: root.querySelector('[data-rf-form]'),
        reset: root.querySelector('[data-rf-reset]'),
        chips: root.querySelector('[data-rf-chips]'),
        count: root.querySelector('[data-rf-count]'),
        progress: root.querySelector('[data-rf-progress]'),
        warning: root.querySelector('[data-rf-warning]'),
        statusbar: root.querySelector('[data-rf-statusbar]'),
        grid: root.querySelector('[data-rf-grid]'),
        empty: root.querySelector('[data-rf-empty]'),
        more: root.querySelector('[data-rf-more]'),
        mapEl: root.querySelector('[data-rf-map]'),
        mapLoading: root.querySelector('[data-rf-map-loading]'),
        filters: root.querySelector('[data-rf-filters]'),
        openFilters: root.querySelector('[data-rf-open-filters]'),
        closeFilters: root.querySelector('[data-rf-close-filters]'),
        fsheetApply: root.querySelector('[data-rf-fsheet-apply]'),
        filterCount: root.querySelector('[data-rf-filter-count]'),
        quickReset: root.querySelector('[data-rf-quick-reset]'),
        backdrop: root.querySelector('[data-rf-backdrop]'),
        sheet: root.querySelector('[data-rf-sheet]'),
        sheetBackdrop: root.querySelector('[data-rf-sheet-backdrop]'),
        sheetTitle: root.querySelector('[data-rf-sheet-title]'),
        sheetMedia: root.querySelector('[data-rf-sheet-media]'),
        sheetVariants: root.querySelector('[data-rf-sheet-variants]'),
        sheetLink: root.querySelector('[data-rf-sheet-link]'),
        sheetClose: root.querySelector('[data-rf-sheet-close]'),
        debug: root.querySelector('[data-rf-debug]'),
        selects: {},
      };
      FACETS.forEach((facet) => {
        this.els.selects[facet] = root.querySelector('[data-rf-select="' + facet + '"]');
      });

      this.bindEvents();
      this.observe();
      this.setupLogoAlign();
      this.setupMapDock();
      this.applyView();
      root.raceFinder = this; // debugging/support handle

      // Deep link from the Race Search section (?rf_q=<query>): land with the
      // search already applied and the grid view active.
      const deepLink = /[?&]rf_q=([^&]*)/.exec(window.location.search);
      if (deepLink) {
        const q = decodeURIComponent(deepLink[1].replace(/\+/g, ' ')).trim();
        if (q) {
          this.startLoad();
          this.applySearch(q);
        }
      }
    }

    // Load state is owned by the shared IndexLoader; exposed here because
    // rendering reads it in many places.
    get loadStarted() {
      return this.loader.loadStarted;
    }

    get loadComplete() {
      return this.loader.loadComplete;
    }

    get failedCollections() {
      return this.loader.failedCollections;
    }

    get collectionStats() {
      return this.loader.collectionStats;
    }

    /* ---------- map/grid view toggle ---------- */

    applyView() {
      this.root.classList.toggle('rf--view-map', this.view === 'map');
      this.root.classList.toggle('rf--view-grid', this.view === 'grid');
      // The toggle button always offers the INACTIVE view; the visible
      // icon/label swap is pure CSS off the root class, only the accessible
      // name needs updating here.
      const toggle = this.root.querySelector('[data-rf-view-toggle]');
      if (toggle) {
        toggle.setAttribute(
          'aria-label',
          this.view === 'map'
            ? this.strings.view_switch_grid
            : this.strings.view_switch_map
        );
      }
    }

    setView(view) {
      if (view !== 'map' && view !== 'grid') return;
      if (view === this.view) return;
      this.view = view;
      sessionSet('rf:view', view);
      this.applyView();
      if (view === 'map') {
        // Map view resets the search (dropdown filters stay active).
        if (this.els.q) this.els.q.value = '';
        this.ac.close();
        if (this.state.q) this.setState({ q: '' });
        this.attemptMapInit();
        // The container was display:none — the canvas needs a resize.
        if (this.map) this.map.resize();
      }
    }

    /* ---------- search autocomplete (from the local index) ----------
       The dropdown mechanics live in the shared Autocomplete class (see
       race-search-core.js); this section only supplies the CONTENT — and,
       unlike the Race Search section, it bundles Dark/Light editions into
       one suggestion and applies picks as a grid search instead of
       navigating. The instance is created in bindEvents(). */

    getAcItems(raw) {
      this.ensureSorted();
      // Respect the active dropdown filters so suggestions match what the
      // grid will show.
      const probe = Object.assign({}, this.state, { q: raw });
      const candidates = this.filterRecords(probe);
      // Bundle Dark/Light editions into one suggestion per race.
      const groups = new Map();
      candidates.forEach((record) => {
        const key = normalizeText(raceBaseTitle(record.t));
        if (!groups.has(key)) groups.set(key, record);
      });
      const items = Array.from(groups.values())
        .slice(0, 8)
        .map((record) => {
          const base = raceBaseTitle(record.t);
          return {
            img: record.i || null,
            title: base,
            sub: [record.c, record.d ? this.distanceLabel(record.d) : null]
              .filter(Boolean)
              .join(' \u00b7 '),
            pick: base,
          };
        });
      // The bottom row applies the raw typed query; with zero matches the
      // dropdown offers the configured fallback products instead (the
      // customizable "any race" frames \u2014 picks navigate to the product).
      return {
        items,
        total: candidates.length,
        allPick: raw,
        fallback: candidates.length === 0 ? fallbackAcItems(this.config) : null,
      };
    }

    /* Applies a search inside the finder: the term stays in the field, the
       view always switches to the grid with the matching results. */
    applySearch(q) {
      this.els.q.value = q;
      this.setState({ q: q.trim() });
      this.setView('grid');
      this.ac.close();
      // Dismiss the mobile keyboard and block any tap-synthesized focus
      // event from immediately reopening the suggestions.
      this.ac.suppress(500);
      this.els.q.blur();
    }

    /* Aligns the content's left edge (and the map's filter overlay) with the
       header logo on desktop — same mechanism as the theme's search-field
       section. No-op when disabled, when the selector matches nothing, or
       below 750px. */
    /* Soft dock (mobile, map view): when a scroll ENDS with the map's top
       edge close to the sticky header's bottom edge (small radius), the
       page glides the last few pixels so the map sits exactly below the
       header. Replaces CSS proximity snap, which grabbed from half a
       viewport away on iOS and could not be tuned. */
    setupMapDock() {
      const RADIUS = 80;
      const headerH = parseInt(this.config.stickyHeader, 10) || 0;
      const media = window.matchMedia('(max-width: 749px)');
      const wrap = this.root.querySelector('.rf__map-wrap');
      if (!wrap) return;
      let timer = null;
      let docking = false;
      const tryDock = () => {
        if (docking || !media.matches || this.view !== 'map') return;
        const delta = wrap.getBoundingClientRect().top - headerH;
        if (Math.abs(delta) <= RADIUS && Math.abs(delta) > 2) {
          docking = true;
          window.scrollBy({ top: delta, behavior: this.reducedMotion ? 'auto' : 'smooth' });
          window.setTimeout(() => {
            docking = false;
          }, 600);
        }
      };
      this.dockScroll = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(tryDock, 160);
      };
      window.addEventListener('scroll', this.dockScroll, { passive: true });
    }

    setupLogoAlign() {
      if (!this.config.autoAlign) return;
      this.logoAlign = createLogoAlign({
        root: this.root,
        selector: this.config.logoSelector,
      });
    }

    /* ---------- setup ---------- */

    bindEvents() {
      this.els.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.startLoad();
        this.applySearch(this.els.q.value.trim());
        this.closeSheet();
        this.els.statusbar.scrollIntoView({
          behavior: this.reducedMotion ? 'auto' : 'smooth',
          block: 'start',
        });
      });

      // Suggestion dropdown: mechanics (debounce, keyboard nav, pointerdown
      // delegation, focus-reopen suppression) live in the shared Autocomplete
      // class; this section supplies content + pick behavior.
      this.ac = new Autocomplete({
        input: this.els.q,
        container: this.els.ac,
        scope: this.els.q.closest('.rf__field--search'),
        enabled: Boolean(this.config.autocomplete),
        idPrefix: 'rf-opt-' + (this.config.sectionId || 'x') + '-',
        strings: {
          empty: this.strings.ac_empty,
          allOne: this.strings.ac_all_one,
          allMany: this.strings.ac_all,
          fallbackLabel: this.strings.ac_fallback,
        },
        formatNumber: (n) => this.numberFormat.format(n),
        canOpen: () => this.loadStarted,
        onType: () => this.startLoad(),
        onClear: () => {
          // Clearing the field live-resets the search: show everything again.
          if (this.state.q) this.setState({ q: '' });
        },
        getItems: (raw) => this.getAcItems(raw),
        onPick: (pick) => {
          // Fallback-product picks navigate; race picks apply as searches.
          if (pick && pick.url) {
            window.location.assign(pick.url);
            return;
          }
          this.applySearch(pick);
        },
      });

      if (this.els.clear) {
        this.els.clear.addEventListener('click', () => {
          this.els.q.value = '';
          this.ac.close();
          if (this.state.q) this.setState({ q: '' });
          this.els.q.focus();
        });
      }

      FACETS.forEach((facet) => {
        this.els.selects[facet].addEventListener('change', (event) => {
          const value = event.target.value || null;
          this.setState({ [facet]: value });
          if (facet === 'country' && value) this.flyToCountry(value);
        });
      });

      this.els.reset.addEventListener('click', () => {
        this.els.q.value = '';
        this.setState({ q: '', racetype: null, region: null, country: null });
      });

      this.els.chips.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-rf-chip-remove]');
        if (!btn) return;
        const facet = btn.getAttribute('data-rf-chip-remove');
        if (facet === 'q') this.els.q.value = '';
        this.setState({ [facet]: facet === 'q' ? '' : null });
      });

      this.els.more.addEventListener('click', () => {
        this.gridPages += 1;
        this.renderGrid();
      });

      if (this.els.openFilters) {
        this.els.openFilters.addEventListener('click', () => this.openSheet());
      }
      if (this.els.closeFilters) {
        this.els.closeFilters.addEventListener('click', () => this.closeSheet());
      }
      if (this.els.fsheetApply) {
        this.els.fsheetApply.addEventListener('click', () => this.closeSheet());
      }
      if (this.els.quickReset) {
        // Same full reset as the sheet's "Reset all" — single source of truth.
        this.els.quickReset.addEventListener('click', () => this.els.reset.click());
      }

      const viewToggle = this.root.querySelector('[data-rf-view-toggle]');
      if (viewToggle) {
        viewToggle.addEventListener('click', () => {
          this.setView(this.view === 'map' ? 'grid' : 'map');
        });
      }
      if (this.els.backdrop) {
        this.els.backdrop.addEventListener('click', () => this.closeSheet());
      }
      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeSheet();
          this.closeProductSheet();
        }
      });

      if (this.els.sheetClose) {
        this.els.sheetClose.addEventListener('click', () => this.closeProductSheet());
      }
      if (this.els.sheetBackdrop) {
        this.els.sheetBackdrop.addEventListener('click', () => this.closeProductSheet());
      }

      // First interaction with the filter bar also starts the index load.
      this.els.filters.addEventListener('focusin', () => this.startLoad(), { once: true });
    }

    observe() {
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.inView = true;
            this.startLoad();
            this.attemptMapInit();
          });
        },
        { rootMargin: '400px 0px' }
      );
      this.io.observe(this.root);
    }

    destroy() {
      if (this.io) this.io.disconnect();
      if (this.cardsIo) this.cardsIo.disconnect();
      if (this.dockScroll) window.removeEventListener('scroll', this.dockScroll);
      if (this.ac) this.ac.destroy();
      if (this.logoAlign) this.logoAlign.destroy();
      this.closeProductSheet();
      if (this.map) {
        this.map.remove();
        this.map = null;
        this.mapReady = false;
      }
    }

    /* ---------- index loading (shared IndexLoader; UI wiring here) ---------- */

    startLoad() {
      if (this.loader.loadStarted) return;
      const started = this.loader.start();
      // Cache hits complete synchronously — no progress line for those.
      if (!this.loader.loadComplete) this.els.progress.hidden = false;
      started.then(() => {
        this.els.progress.hidden = true;
        this.renderWarning();
        this.refresh();
        this.attemptMapInit();
        this.loadThemeCards();
      });
    }

    /* Theme product cards: fetched from /collections/all via
       sections/race-finder-cards.liquid, AFTER the index is complete so they
       never compete with the (much smaller) index requests. Page 1 (50 cards,
       covers the "preload at least 20" requirement) loads immediately; all
       remaining pages load only once the visitor scrolls toward the grid.
       Cards are kept in memory only — too large for sessionStorage. A record
       whose card has not arrived (yet, or at all) renders the built-in
       fallback card; refresh() swaps theme cards in as pages land. */
    async loadThemeCards() {
      if (!this.config.useThemeCards || this.cardsStarted) return;
      this.cardsStarted = true;
      const routesRoot =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const base =
        routesRoot + 'collections/all?section_id=' +
        this.config.cardsSectionId + '&page=';
      let pages;
      try {
        pages = await this.fetchCardsPage(base, 1); // preload
      } catch (e) {
        return; // fallback cards stay — theme cards are progressive enhancement
      }
      if (pages <= 1) return;
      this.cardsIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || !this.cardsIo) return;
            this.cardsIo.disconnect();
            this.cardsIo = null;
            this.loadRemainingCards(base, pages);
          });
        },
        { rootMargin: '600px 0px' }
      );
      this.cardsIo.observe(this.els.grid);
    }

    async loadRemainingCards(base, pages) {
      const queue = [];
      for (let p = 2; p <= pages; p++) queue.push(p);
      const workers = [];
      for (let i = 0; i < 2; i++) {
        workers.push(
          (async () => {
            while (queue.length > 0) {
              const page = queue.shift();
              try {
                await this.fetchCardsPage(base, page);
              } catch (e) {
                /* this page's records keep their fallback cards */
              }
            }
          })()
        );
      }
      await Promise.all(workers);
    }

    async fetchCardsPage(base, page) {
      const response = await fetch(base + page);
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      doc.querySelectorAll('[data-rf-card-handle]').forEach((li) => {
        const handle = li.getAttribute('data-rf-card-handle');
        if (!this.themeCards.has(handle)) this.themeCards.set(handle, li);
      });
      this.scheduleRefresh();
      const wrap = doc.querySelector('[data-rf-cards]');
      return wrap ? parseInt(wrap.getAttribute('data-rf-pages'), 10) || 1 : 1;
    }

    retryFailed() {
      const retried = this.loader.retryFailed();
      this.renderWarning();
      this.els.progress.hidden = false;
      this.renderProgress();
      retried.then(() => {
        this.els.progress.hidden = true;
        this.renderWarning();
        this.refresh();
      });
    }

    /* ---------- filter engine ---------- */

    setState(partial) {
      const searchCleared = partial.q === '' && this.state.q !== '';
      Object.assign(this.state, partial);
      this.gridPages = 1;
      this.refresh();
      // Anyone mirroring the search term (the sticky header search bar)
      // syncs on this — fired for EVERY way the search empties: map-view
      // switch, Reset all, removing the search chip, clear button.
      if (searchCleared) {
        this.root.dispatchEvent(new CustomEvent('rf:search-cleared', { bubbles: true }));
      }
    }

    recordMatches(record, state) {
      // Free-text matching (haystack + query aliases) is shared with the
      // Race Search section — see matchesQuery in race-search-core.js.
      if (state.q && !matchesQuery(record, state.q, (key) => this.distanceLabel(key))) {
        return false;
      }
      if (state.country && record.cc !== state.country) return false;
      if (state.region && record.r !== state.region) return false;
      if (state.racetype) {
        const sep = state.racetype.indexOf(':');
        const kind = state.racetype.slice(0, sep);
        const value = state.racetype.slice(sep + 1);
        if (kind === 'col') {
          if (!record.cols.has(value) && record.ty !== value) return false;
        } else if (kind === 'dist') {
          if (record.d !== value) return false;
        } else if (record.ty !== value) {
          return false;
        }
      }
      return true;
    }

    filterRecords(state) {
      return this.sortedRecords.filter((record) => this.recordMatches(record, state));
    }

    ensureSorted() {
      if (!this.sortDirty) return;
      this.sortedRecords = Array.from(this.records.values()).sort((a, b) =>
        a.t.localeCompare(b.t, 'en')
      );
      this.sortDirty = false;
    }

    /* ---------- labels ---------- */

    distanceLabel(key) {
      return this.labels.distance[key] || key;
    }

    regionLabel(key) {
      return this.labels.region[key] || key;
    }

    countryLabel(code) {
      const block = this.collections.find((c) => c.cc === code);
      if (block && block.label) return block.label;
      try {
        const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code);
        if (name && name !== code) return name;
      } catch (e) {
        /* unknown code — fall through */
      }
      return code;
    }

    racetypeLabel(value) {
      const sep = value.indexOf(':');
      const kind = value.slice(0, sep);
      const key = value.slice(sep + 1);
      if (kind === 'col') {
        const block = this.collectionByHandle.get(key);
        if (block && block.label) return block.label;
      }
      if (kind === 'dist') return this.distanceLabel(key);
      return TYPE_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
    }

    facetValue(record, facet) {
      if (facet === 'region') return record.r;
      if (facet === 'country') return record.cc;
      return record.ty;
    }

    /* ---------- rendering ---------- */

    scheduleRefresh() {
      if (this.refreshQueued) return;
      this.refreshQueued = true;
      window.requestAnimationFrame(() => {
        this.refreshQueued = false;
        this.refresh();
      });
    }

    refresh() {
      this.ensureSorted();
      this.matches = this.filterRecords(this.state);
      this.renderCount();
      this.renderProgress();
      this.renderChips();
      this.renderOptions();
      this.renderGrid();
      this.renderMapData();
      this.renderDebug();
    }

    renderCount() {
      const n = this.matches.length;
      let text;
      if (n === 0) text = this.strings.found_zero;
      else if (n === 1) text = this.strings.found_one;
      else text = fmt(this.strings.found_many, { count: this.numberFormat.format(n) });
      // Desktop badge shows JUST the number; the full wording lives in a
      // visually hidden span so the aria-live region keeps announcing it.
      const badgeNum = document.createElement('span');
      badgeNum.setAttribute('aria-hidden', 'true');
      badgeNum.textContent = this.numberFormat.format(n);
      const badgeSr = document.createElement('span');
      badgeSr.className = 'rf__label';
      badgeSr.textContent = text;
      this.els.count.replaceChildren(badgeNum, badgeSr);
      if (this.els.fsheetApply) {
        this.els.fsheetApply.textContent =
          n === 1
            ? this.strings.show_results_one
            : fmt(this.strings.show_results, { count: this.numberFormat.format(n) });
      }
      if (this.els.filterCount) {
        // Mobile filter button: the count renders as a compact pill badge
        // (just the number); the full wording stays available to screen
        // readers through the button's aria-label.
        this.els.filterCount.textContent = this.numberFormat.format(n);
        if (this.els.openFilters) {
          const wording =
            n === 1
              ? this.strings.filter_count_one
              : fmt(this.strings.filter_count, { count: this.numberFormat.format(n) });
          this.els.openFilters.setAttribute('aria-label', (this.strings.filter || 'Filter') + ' ' + wording);
        }
      }
      // Anything active? Drives the mobile quick reset AND the desktop
      // "Reset all" button (CSS shows it via this root class).
      const active = !!(
        this.state.q ||
        this.state.racetype ||
        this.state.region ||
        this.state.country
      );
      this.root.classList.toggle('rf--has-filters', active);
      if (this.els.quickReset) {
        this.els.quickReset.hidden = !active;
      }
    }

    renderProgress() {
      if (this.loadComplete || !this.loadStarted) return;
      this.els.progress.textContent = fmt(this.strings.loading, {
        count: this.numberFormat.format(this.records.size),
      });
    }

    renderWarning() {
      const failed = this.failedCollections.size;
      this.els.warning.hidden = failed === 0;
      this.els.warning.textContent = '';
      if (failed === 0) return;
      this.els.warning.append(
        fmt(this.strings.load_error, { count: this.numberFormat.format(failed) }) + ' '
      );
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'rf__link-btn';
      retry.textContent = this.strings.retry;
      retry.addEventListener('click', () => this.retryFailed());
      this.els.warning.append(retry);
    }

    renderChips() {
      this.els.chips.textContent = '';
      const chips = [];
      if (this.state.q) chips.push({ facet: 'q', label: '“' + this.state.q + '”' });
      if (this.state.racetype)
        chips.push({ facet: 'racetype', label: this.racetypeLabel(this.state.racetype) });
      if (this.state.region)
        chips.push({ facet: 'region', label: this.regionLabel(this.state.region) });
      if (this.state.country)
        chips.push({ facet: 'country', label: this.countryLabel(this.state.country) });

      chips.forEach((chip) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'rf__chip';
        el.setAttribute('data-rf-chip-remove', chip.facet);
        el.setAttribute('aria-label', fmt(this.strings.remove_filter, { label: chip.label }));
        const label = document.createElement('span');
        label.textContent = chip.label;
        const x = document.createElement('span');
        x.className = 'rf__chip-x';
        x.setAttribute('aria-hidden', 'true');
        x.textContent = '✕';
        el.append(label, x);
        this.els.chips.append(el);
      });
    }

    // Option values exist if any loaded record has them; counts and disabling
    // are computed against the OTHER active filters (classic faceting).
    optionEntries(facet) {
      const otherState = Object.assign({}, this.state, { [facet]: null });
      const base = this.filterRecords(otherState);

      if (facet === 'racetype') {
        // Race type merges three sources: type collections (block order),
        // distance keys (canonical order) and racetype tags (alphabetical).
        // Deduped by label in that priority — a collection whose label
        // equals a distance label absorbs that distance entry.
        const entries = [];
        const seenLabels = new Set();
        const push = (value, count) => {
          const label = this.racetypeLabel(value);
          const labelKey = normalizeText(label);
          if (seenLabels.has(labelKey)) return;
          seenLabels.add(labelKey);
          entries.push({ value, label, count });
        };
        const typeCollections = this.collections.filter((c) => !c.cc);
        const collectionKeys = new Set(typeCollections.map((c) => c.handle));
        typeCollections.forEach((c) => {
          const exists = this.sortedRecords.some(
            (r) => r.cols.has(c.handle) || r.ty === c.handle
          );
          if (!exists) return;
          push(
            'col:' + c.handle,
            base.filter((r) => r.cols.has(c.handle) || r.ty === c.handle).length
          );
        });
        const distKeys = new Set();
        this.sortedRecords.forEach((r) => {
          if (r.d) distKeys.add(r.d);
        });
        Array.from(distKeys)
          .sort((a, b) => {
            const ai = DISTANCE_ORDER.indexOf(a);
            const bi = DISTANCE_ORDER.indexOf(b);
            if (ai !== -1 || bi !== -1) {
              return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
            }
            return this.distanceLabel(a).localeCompare(this.distanceLabel(b), 'en');
          })
          .forEach((key) => {
            push('dist:' + key, base.filter((r) => r.d === key).length);
          });
        const tagKeys = new Set();
        this.sortedRecords.forEach((r) => {
          if (r.ty && !collectionKeys.has(r.ty)) tagKeys.add(r.ty);
        });
        Array.from(tagKeys)
          .sort((a, b) =>
            this.racetypeLabel('tag:' + a).localeCompare(this.racetypeLabel('tag:' + b), 'en')
          )
          .forEach((key) => {
            push('tag:' + key, base.filter((r) => r.ty === key).length);
          });
        return entries;
      }

      const values = new Set();
      this.sortedRecords.forEach((r) => {
        const v = this.facetValue(r, facet);
        if (v) values.add(v);
      });
      const counts = new Map();
      base.forEach((r) => {
        const v = this.facetValue(r, facet);
        if (v) counts.set(v, (counts.get(v) || 0) + 1);
      });
      const labelFor = (v) =>
        facet === 'region' ? this.regionLabel(v) : this.countryLabel(v);
      const list = Array.from(values).map((v) => ({
        value: v,
        label: labelFor(v),
        count: counts.get(v) || 0,
      }));
      list.sort((a, b) => a.label.localeCompare(b.label, 'en'));
      return list;
    }

    renderOptions() {
      FACETS.forEach((facet) => {
        const select = this.els.selects[facet];
        const current = this.state[facet] || '';
        select.textContent = '';
        const allOption = document.createElement('option');
        allOption.value = '';
        allOption.textContent = this.strings['all_' + facet];
        select.append(allOption);
        this.optionEntries(facet).forEach((entry) => {
          const option = document.createElement('option');
          option.value = entry.value;
          option.textContent = entry.label + ' (' + this.numberFormat.format(entry.count) + ')';
          if (entry.count === 0 && entry.value !== current) option.disabled = true;
          if (entry.value === current) option.selected = true;
          select.append(option);
        });
        select.value = current;
      });
    }

    productUrl(record) {
      const routesRoot =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return routesRoot + 'products/' + record.h;
    }

    buildCard(record) {
      const themeCard = this.themeCards.get(record.h);
      const cached = this.cardCache.get(record.h);
      // Reuse the cached node unless a theme card has arrived that should
      // replace a fallback card.
      if (cached && (cached.getAttribute('data-rf-theme') === '1' || !themeCard)) {
        return cached;
      }
      if (themeCard) {
        const themeLi = themeCard.cloneNode(true);
        themeLi.classList.add('rf__card-item');
        themeLi.setAttribute('data-rf-theme', '1');
        themeLi.setAttribute('data-rf-handle', record.h);
        this.cardCache.set(record.h, themeLi);
        return themeLi;
      }
      const li = document.createElement('li');
      li.className = 'rf__card-item collection-product-card';
      li.setAttribute('data-rf-handle', record.h);
      const a = document.createElement('a');
      a.className = 'rf__card';
      a.href = this.productUrl(record);

      const media = document.createElement('div');
      media.className = 'rf__card-media';
      if (record.i) {
        const img = document.createElement('img');
        img.src = record.i;
        img.alt = record.t;
        img.loading = 'lazy';
        img.width = 400;
        img.height = 400;
        media.append(img);
      } else {
        media.classList.add('rf__card-media--empty');
      }

      const body = document.createElement('div');
      body.className = 'rf__card-body';
      const title = document.createElement('h3');
      title.className = 'rf__card-title';
      title.textContent = record.t;
      body.append(title);
      if (record.c) {
        const city = document.createElement('p');
        city.className = 'rf__card-city';
        city.textContent = record.c;
        body.append(city);
      }
      if (record.d) {
        const badge = document.createElement('span');
        badge.className = 'rf__badge';
        badge.textContent = this.distanceLabel(record.d);
        body.append(badge);
      }

      a.append(media, body);
      li.append(a);
      this.cardCache.set(record.h, li);
      return li;
    }

    renderGrid() {
      const visible = this.matches.slice(0, this.gridPages * GRID_PAGE_SIZE);
      const fragment = document.createDocumentFragment();
      visible.forEach((record) => fragment.append(this.buildCard(record)));
      this.els.grid.textContent = '';
      this.els.grid.append(fragment);
      this.renderedCount = visible.length;

      this.els.empty.hidden = !(this.loadComplete && this.matches.length === 0);
      const remaining = this.matches.length - visible.length;
      this.els.more.hidden = remaining <= 0;
      if (remaining > 0) {
        this.els.more.textContent = fmt(this.strings.load_more, {
          count: this.numberFormat.format(remaining),
        });
      }
    }

    highlightCard(handle) {
      const index = this.matches.findIndex((r) => r.h === handle);
      if (index === -1) return;
      let changed = false;
      while (this.gridPages * GRID_PAGE_SIZE <= index) {
        this.gridPages += 1;
        changed = true;
      }
      if (changed || this.renderedCount <= index) this.renderGrid();
      const card = this.els.grid.querySelector(
        '[data-rf-handle="' + (window.CSS ? CSS.escape(handle) : handle) + '"]'
      );
      if (!card) return;
      card.scrollIntoView({
        behavior: this.reducedMotion ? 'auto' : 'smooth',
        block: 'center',
      });
      card.classList.add('rf__card--active');
      window.setTimeout(() => card.classList.remove('rf__card--active'), 2500);
    }

    /* ---------- map ---------- */

    async attemptMapInit() {
      if (this.map || this.mapLoading) return;
      // Grid view: MapLibre and the land data load only when the map shows.
      if (!this.inView || !this.loadStarted || this.view !== 'map') return;
      this.mapLoading = true;
      this.updateMapLoading();
      try {
        await loadStylesheet(this.config.assets.maplibreCss);
        await loadScript(this.config.assets.maplibreJs);
        this.initMap(); // data/bounds follow in the map's 'load' handler
      } catch (e) {
        /* asset load or WebGL failure — placeholder area stays, retried on next IO hit */
      }
      this.mapLoading = false;
      this.updateMapLoading();
    }

    initMap() {
      const maplibregl = window.maplibregl;
      const mode = this.config.mapStyle === 'dark' ? 'dark' : 'light';
      // Map ground: the "Map background" setting wins (default: a subtle
      // grey so the dots sit on a light tinted ground); when cleared, the
      // section background is used, with a per-palette fallback if that is
      // transparent.
      let bg = this.config.mapBackground;
      if (!bg) {
        bg = getComputedStyle(this.root).backgroundColor;
        if (!bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)') {
          bg = MAP_BG_FALLBACK[mode];
        }
      }
      // Zoom-out floor: the section setting decides how far out visitors
      // can go (default ~continent overview); the world-width fit acts as
      // the absolute lower bound so a value of 0 means "whole world fills
      // the screen". Recomputed on resize (the width fit is viewport-
      // dependent).
      const settingMinZoom = parseFloat(this.config.mapMinZoom);
      const worldMinZoom = () =>
        Math.max(
          Number.isFinite(settingMinZoom) ? settingMinZoom : 1.5,
          Math.log2(Math.max(this.els.mapEl.clientWidth, 128) / 512)
        );
      this.map = new maplibregl.Map({
        container: this.els.mapEl,
        style: dottedMapStyle(bg),
        center: [10, 30],
        zoom: 1.6,
        // Race discovery, not navigation: zooming IN stops at 8 (the 50m
        // coastlines keep shapes acceptable up to there).
        minZoom: worldMinZoom(),
        maxZoom: 8,
        attributionControl: false,
        cooperativeGestures: true,
        fadeDuration: this.reducedMotion ? 0 : 300,
      });
      this.map.on('resize', () => this.map.setMinZoom(worldMinZoom()));
      // No attribution control: Natural Earth data is public domain and the
      // map makes no third-party requests anymore.
      this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }));
      this.map.dragRotate.disable();
      this.map.touchZoomRotate.disableRotation();

      // First touch/scroll on the map (incl. the zoom buttons) means the
      // visitor is exploring — the deferred auto-fit below must not yank
      // the camera away anymore.
      const markUserMove = () => {
        this.userMovedMap = true;
      };
      this.els.mapEl.addEventListener('pointerdown', markUserMove, { once: true, capture: true });
      this.els.mapEl.addEventListener('wheel', markUserMove, { once: true, capture: true, passive: true });

      this.map.on('load', () => {
        const accent = this.config.pinColor || '#e2001a';

        // Halftone land, hybrid: whole-dot grids for the world/continent
        // views (one resolution per zoom band, density resets per band),
        // constant-density fill-pattern on the 50m land polygons from
        // zoom 4. Added before the races layers, so clusters/pins always
        // draw on top. The 1MB detail polygons load LAZILY on the first
        // zoom past 3.5 — world-view-only visitors never fetch them.
        const dotColor = this.config.mapDotColor || MAP_DOT_COLORS[mode];
        // Subtle land tint UNDER the dots (ocean keeps the map background):
        // 110m polygons for the overview zooms; the detail fill on the 50m
        // polygons joins in ensureDetailLand below. Empty setting = no tint.
        const landColor = this.config.mapLandColor || '';
        if (landColor) {
          this.map.addSource('land110', { type: 'geojson', data: this.config.assets.land110 });
          this.map.addLayer({
            id: 'rf-land-fill-low', type: 'fill', source: 'land110',
            maxzoom: 3.5,
            paint: { 'fill-color': landColor },
          });
        }
        MAP_DOT_BANDS.forEach((band) => {
          this.map.addSource(band.key, { type: 'geojson', data: this.config.assets[band.key] });
          const layer = {
            id: band.id, type: 'circle', source: band.key,
            maxzoom: band.maxzoom,
            paint: { 'circle-color': dotColor, 'circle-radius': band.radius },
          };
          // The world band must also cover NEGATIVE zoom (phones fit the
          // world below zoom 0) — only inner bands get a lower bound.
          if (band.minzoom > 0) layer.minzoom = band.minzoom;
          this.map.addLayer(layer);
        });
        const ensureDetailLand = () => {
          if (this.detailLandAdded || this.map.getZoom() < 3.2) return;
          this.detailLandAdded = true;
          const dots = makeDotPattern(dotColor);
          this.map.addImage('rf-land-dot', dots.image, { pixelRatio: dots.pixelRatio });
          this.map.addSource('land50', { type: 'geojson', data: this.config.assets.land50 });
          if (landColor) {
            this.map.addLayer(
              {
                id: 'rf-land-fill-detail', type: 'fill', source: 'land50',
                minzoom: 3.5,
                paint: { 'fill-color': landColor },
              },
              'rf-clusters'
            );
          }
          this.map.addLayer(
            {
              id: 'rf-land-detail', type: 'fill', source: 'land50',
              minzoom: 3.5,
              paint: { 'fill-pattern': 'rf-land-dot' },
            },
            'rf-clusters' // keep clusters/pins above
          );
        };
        this.map.on('zoomend', ensureDetailLand);
        this.map.on('moveend', ensureDetailLand);
        // Subtle city labels from zoom 4 — names render as canvas IMAGES
        // (rf-city-* via styleimagemissing), never as text layers: glyph
        // PBFs cannot be hosted as Shopify assets and a failed glyph fetch
        // drops whole tiles (the round-5 lesson). Data: self-hosted Natural
        // Earth populated places; each city carries its own reveal zoom in
        // `z`, so more names appear the closer you get. Loaded lazily like
        // the 50m land detail — world-view visitors never fetch it.
        const ensureCityLabels = () => {
          if (this.cityLabelsAdded || !this.config.cityLabels) return;
          if (this.map.getZoom() < 3.7) return;
          this.cityLabelsAdded = true;
          this.map.addSource('cities', {
            type: 'geojson',
            data: this.config.assets.mapCities,
          });
          this.map.addLayer(
            {
              id: 'rf-city-labels', type: 'symbol', source: 'cities',
              minzoom: 4,
              // ["zoom"] in filters is re-evaluated at integer zooms — good
              // enough for a progressive reveal.
              filter: ['<=', ['get', 'z'], ['zoom']],
              layout: {
                'icon-image': ['concat', 'rf-city-', ['get', 'n']],
                'icon-anchor': 'center',
                'icon-padding': 6,
              },
            },
            'rf-clusters' // labels stay below clusters and pins
          );
        };
        this.map.on('zoomend', ensureCityLabels);
        this.map.on('moveend', ensureCityLabels);

        this.map.addSource('races', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
          cluster: true,
          // Fully unclustered one step before maxZoom — with the old value
          // (13 > maxZoom) co-located pins could never leave their cluster.
          // Radius 40 keeps the world/continent views lively (60 collapsed
          // whole continents into single mega clusters at low zoom).
          clusterRadius: 40,
          clusterMaxZoom: 7,
        });
        this.map.addLayer({
          id: 'rf-clusters', type: 'circle', source: 'races',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': accent,
            'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 50, 26, 200, 32],
            'circle-stroke-color': '#ffffff',
            'circle-stroke-opacity': 0.25,
            'circle-stroke-width': 5,
          },
        });
        // Counts are IMAGES, not text: a text-field would need a glyphs
        // endpoint, which Shopify's asset store cannot host (and a failed
        // glyph fetch takes the whole tile down with it — clusters
        // included). styleimagemissing rasterizes each abbreviated count
        // once, synchronously, via canvas.
        this.map.addLayer({
          id: 'rf-cluster-count', type: 'symbol', source: 'races',
          filter: ['has', 'point_count'],
          layout: {
            'icon-image': ['concat', 'rf-count-', ['get', 'point_count_abbreviated']],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
        });
        // Custom SVG pin (brand-colored teardrop, white inner circle with the
        // race abbreviation, soft ground shadow). One image per abbreviation,
        // rasterized at 2x from an inline SVG; unknown abbreviations are
        // generated on demand via styleimagemissing.
        const PIN_W = 42;
        const PIN_H = 54;
        const pinSvg = (abbrev) => {
          const safe = String(abbrev)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
          const fontSize =
            safe.length >= 5 ? 9 : safe.length >= 4 ? 10.5 : safe.length === 3 ? 12 : 13;
          return (
            '<svg width="' + PIN_W * 2 + '" height="' + PIN_H * 2 + '" viewBox="0 0 42 54" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<g filter="url(#rfps)"><ellipse cx="21" cy="49.5" rx="7" ry="2.5" fill="black" fill-opacity="0.2"/></g>' +
            '<path fill-rule="evenodd" clip-rule="evenodd" d="M21 0C32.598 0 42 9.40202 42 21C42 29.8926 36.4727 37.4942 28.6663 40.5567L22.1961 49.4007C22.0612 49.5858 21.8821 49.7369 21.6739 49.8412C21.4657 49.9455 21.2346 50 21 50C20.7654 50 20.5343 49.9455 20.3261 49.8412C20.1179 49.7369 19.9388 49.5858 19.8039 49.4007L13.3337 40.5567C5.52726 37.4941 0 29.8926 0 21C0 9.40202 9.40202 0 21 0Z" fill="' + accent + '"/>' +
            '<path d="M37 21C37 12.1634 29.8366 5 21 5C12.1634 5 5 12.1634 5 21C5 29.8366 12.1634 37 21 37C29.8366 37 37 29.8366 37 21Z" fill="white"/>' +
            (safe
              ? '<text x="21" y="21" text-anchor="middle" dominant-baseline="central" font-family="system-ui, -apple-system, Arial, sans-serif" font-weight="700" font-size="' + fontSize + '" fill="#111111">' + safe + '</text>'
              : '') +
            '<defs><filter id="rfps" x="12" y="45" width="18" height="9" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="1" result="effect1_foregroundBlur"/></filter></defs>' +
            '</svg>'
          );
        };
        const addPinImage = (id) => {
          if (this.map.hasImage(id) || this.pendingPins.has(id)) return;
          this.pendingPins.add(id);
          // Register a transparent placeholder synchronously so MapLibre does
          // not warn per frame while the SVG rasterizes; the real pixels
          // arrive via updateImage right after.
          this.map.addImage(
            id,
            { width: PIN_W * 2, height: PIN_H * 2, data: new Uint8Array(PIN_W * 2 * PIN_H * 2 * 4) },
            { pixelRatio: 2 }
          );
          const img = new Image();
          img.onload = () => {
            this.pendingPins.delete(id);
            if (!this.map) return;
            const canvas = document.createElement('canvas');
            canvas.width = PIN_W * 2;
            canvas.height = PIN_H * 2;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            this.map.updateImage(id, ctx.getImageData(0, 0, canvas.width, canvas.height));
          };
          img.onerror = () => this.pendingPins.delete(id);
          img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(pinSvg(id.slice(7)));
        };
        Object.values(DIST_ABBREV)
          .concat(Object.values(TYPE_ABBREV), [''])
          .forEach((ab) => addPinImage('rf-pin-' + ab));
        // Cluster-count sprite: white bold number on transparent ground,
        // drawn synchronously (no placeholder dance needed). One image per
        // distinct abbreviated count ("2", "48", "1.2k", ...).
        const addCountImage = (id) => {
          if (this.map.hasImage(id)) return;
          const label = id.slice('rf-count-'.length);
          if (!label) return;
          const scale = 2;
          const font = '700 ' + 13 * scale + 'px system-ui, -apple-system, Arial, sans-serif';
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          ctx.font = font;
          canvas.width = Math.max(2, Math.ceil(ctx.measureText(label).width) + 4);
          canvas.height = 16 * scale;
          // resizing the canvas resets the context state — set it again
          ctx.font = font;
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, canvas.width / 2, canvas.height / 2);
          this.map.addImage(
            id,
            ctx.getImageData(0, 0, canvas.width, canvas.height),
            { pixelRatio: scale }
          );
        };
        // City-label sprite: city name on a solid bar (default white bar,
        // black text — matches the shop's sharp black-on-white controls).
        // The bar doubles as the legibility guard over dots and land tint.
        const addCityImage = (id) => {
          if (this.map.hasImage(id)) return;
          const label = id.slice('rf-city-'.length);
          if (!label) return;
          const scale = 2;
          const font = '400 ' + 11 * scale + 'px system-ui, -apple-system, Arial, sans-serif';
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          ctx.font = font;
          const pad = 5 * scale;
          canvas.width = Math.max(2, Math.ceil(ctx.measureText(label).width) + pad * 2);
          canvas.height = 16 * scale;
          ctx.font = font; // canvas resize resets the context state
          ctx.fillStyle = this.config.mapLabelBg || '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = this.config.mapLabelColor || '#000000';
          ctx.fillText(label, canvas.width / 2, canvas.height / 2);
          this.map.addImage(
            id,
            ctx.getImageData(0, 0, canvas.width, canvas.height),
            { pixelRatio: scale }
          );
        };
        this.map.on('styleimagemissing', (event) => {
          if (event.id.indexOf('rf-pin-') === 0) addPinImage(event.id);
          else if (event.id.indexOf('rf-count-') === 0) addCountImage(event.id);
          else if (event.id.indexOf('rf-city-') === 0) addCityImage(event.id);
        });
        this.map.addLayer({
          id: 'rf-point', type: 'symbol', source: 'races',
          filter: ['!', ['has', 'point_count']],
          layout: {
            'icon-image': ['concat', 'rf-pin-', ['get', 'ab']],
            'icon-size': ['interpolate', ['linear'], ['zoom'], 3, 0.65, 10, 0.8, 14, 1],
            'icon-anchor': 'bottom',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
        });
        // HOOK: route polylines. If records ever carry "pl" (a pre-computed,
        // simplified encoded polyline — see README.md), decode it and add a
        // line layer here. Do NOT parse GPX in the browser.

        this.map.on('click', 'rf-clusters', (event) => {
          const feature = event.features[0];
          const source = this.map.getSource('races');
          const result = source.getClusterExpansionZoom(feature.properties.cluster_id);
          const zoomTo = (zoom) =>
            this.map.easeTo({
              center: feature.geometry.coordinates,
              zoom: Math.min(zoom, 8),
              duration: this.reducedMotion ? 0 : 500,
            });
          if (result && typeof result.then === 'function') {
            result.then(zoomTo).catch(() => {});
          }
        });
        this.map.on('click', 'rf-point', (event) => {
          const feature = event.features[0];
          const group = this.mapGroups.get(feature.properties.k);
          if (group && group.length) this.showProductSheet(group);
        });
        ['rf-clusters', 'rf-point'].forEach((layer) => {
          this.map.on('mouseenter', layer, () => {
            this.map.getCanvas().style.cursor = 'pointer';
          });
          this.map.on('mouseleave', layer, () => {
            this.map.getCanvas().style.cursor = '';
          });
        });

        this.mapReady = true;
        this.renderMapData();
        this.fitToMatches(); // frame whatever has loaded so far
        // Cache path: the index was already complete, that fit was final.
        if (this.loadComplete) this.mapFitFinal = true;
        ensureDetailLand(); // the region fit may already sit past zoom 3.5
        ensureCityLabels();
        this.updateMapLoading();
      });
    }

    /* Loading indicator on the map: visible while the map is still coming
       up OR while race pins are still streaming in (the local map now loads
       much faster than the 2000-record index). Never shown when map init
       failed/never started — the placeholder area stays clean. */
    updateMapLoading() {
      if (!this.els.mapLoading) return;
      const busy =
        this.view === 'map' &&
        (this.mapLoading || (this.map && (!this.mapReady || !this.loadComplete)));
      this.els.mapLoading.hidden = !busy;
    }

    /* Product sheet: pin click opens a slide-in overlay (Peak-Design-style
       drawer). Accepts a race GROUP (Dark/Light editions bundled into one
       pin) or a single record; variants are switched via pills. */
    showProductSheet(group) {
      const els = this.els;
      if (!els.sheet) return;
      const records = Array.isArray(group) ? group : [group];
      if (records.length === 0) return;
      this.sheetRecords = records;
      // Title is set per selected variant (full product title incl. edition)
      // in selectSheetVariant.

      if (els.sheetVariants) {
        els.sheetVariants.textContent = '';
        els.sheetVariants.hidden = records.length < 2;
        if (records.length > 1) {
          records.forEach((record, index) => {
            const pill = document.createElement('button');
            pill.type = 'button';
            pill.className = 'rf__sheet-pill';
            pill.textContent = variantLabel(record.t, index, {
              dark: this.strings.variant_dark,
              light: this.strings.variant_light,
              edition: this.strings.variant_edition,
            });
            pill.addEventListener('click', () => this.selectSheetVariant(index));
            els.sheetVariants.append(pill);
          });
        }
      }
      this.selectSheetVariant(0);

      // Only run the open transition / scroll lock when the sheet is not
      // already showing (a second pin click just swaps the content).
      if (els.sheet.hidden) {
        this.sheetReturnFocus = document.activeElement;
        this.bodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        els.sheetBackdrop.hidden = false;
        els.sheet.hidden = false;
        // Force a reflow so the slide-in transition runs from the start state.
        void els.sheet.offsetHeight;
        els.sheet.classList.add('is-open');
        els.sheetBackdrop.classList.add('is-open');
      }
      els.sheetClose.focus();
    }

    selectSheetVariant(index) {
      const els = this.els;
      const record = this.sheetRecords[index];
      if (!record) return;
      this.sheetActiveIndex = index;
      els.sheetTitle.textContent = record.t; // full product title, wraps if needed
      els.sheetLink.href = this.productUrl(record);
      els.sheetMedia.textContent = '';
      if (record.i) {
        const img = document.createElement('img');
        img.src = record.i;
        img.alt = record.t;
        img.loading = 'lazy';
        if (!this.reducedMotion) {
          img.style.opacity = '0';
          img.style.transition = 'opacity 0.25s ease';
          window.requestAnimationFrame(() => {
            img.style.opacity = '1';
          });
        }
        els.sheetMedia.append(img);
      }
      els.sheetMedia.hidden = !record.i;
      if (els.sheetVariants) {
        Array.prototype.forEach.call(els.sheetVariants.children, (pill, i) => {
          pill.classList.toggle('is-active', i === index);
        });
      }
    }

    closeProductSheet() {
      const els = this.els;
      if (!els.sheet || els.sheet.hidden) return;
      els.sheet.classList.remove('is-open');
      els.sheetBackdrop.classList.remove('is-open');
      document.body.style.overflow = this.bodyOverflow || '';
      let closed = false;
      const done = () => {
        if (closed) return;
        closed = true;
        els.sheet.hidden = true;
        els.sheetBackdrop.hidden = true;
      };
      if (this.reducedMotion) {
        done();
      } else {
        els.sheet.addEventListener(
          'transitionend',
          (event) => {
            if (event.target === els.sheet) done();
          },
          { once: true }
        );
        window.setTimeout(done, 600); // safety net if no transition fires
      }
      if (this.sheetReturnFocus && this.sheetReturnFocus.focus) {
        this.sheetReturnFocus.focus();
      }
    }

    renderMapData() {
      if (!this.mapReady) return;
      // Bundle Dark/Light editions of the same race into one pin. Grouping
      // happens after filtering, so a pin shows whenever ANY member matches.
      this.mapGroups = new Map();
      this.matches.forEach((record) => {
        if (typeof record.y !== 'number' || typeof record.x !== 'number') return;
        const key = raceGroupKey(record);
        const group = this.mapGroups.get(key);
        if (group) group.push(record);
        else this.mapGroups.set(key, [record]);
      });
      const features = [];
      this.mapGroups.forEach((group, key) => {
        group.sort(
          (a, b) => variantOrder(a) - variantOrder(b) || a.t.localeCompare(b.t, 'en')
        );
        const primary = group[0];
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [primary.x, primary.y] },
          properties: { k: key, ab: pinAbbrev(primary) },
        });
      });
      // DIFFERENT races at the same spot (a city's Marathon + Half Marathon
      // share the city-center coordinates) would stack exactly. Spread them
      // side by side with a small geographic offset, sized to ~46px at zoom
      // 8 (the SVG pins are 42px wide) — the first level where same-spot
      // pins leave clustering (clusterMaxZoom is 7). Below that they are
      // clustered anyway.
      const offsetDeg = 46 * (360 / (512 * Math.pow(2, 8)));
      const byCoord = new Map();
      features.forEach((feature) => {
        const c = feature.geometry.coordinates;
        const coordKey = c[1].toFixed(5) + ',' + c[0].toFixed(5);
        const list = byCoord.get(coordKey);
        if (list) list.push(feature);
        else byCoord.set(coordKey, [feature]);
      });
      byCoord.forEach((list) => {
        if (list.length < 2) return;
        list.sort((a, b) => a.properties.ab.localeCompare(b.properties.ab, 'en'));
        list.forEach((feature, index) => {
          feature.geometry.coordinates[0] += (index - (list.length - 1) / 2) * offsetDeg;
        });
      });
      const source = this.map.getSource('races');
      if (source) source.setData({ type: 'FeatureCollection', features });

      // The local map is usually ready BEFORE the index finishes — re-frame
      // ONCE when all races have arrived, unless the visitor already moved.
      if (this.loadComplete && !this.mapFitFinal) {
        this.mapFitFinal = true;
        if (!this.userMovedMap) this.fitToMatches();
      }
      this.updateMapLoading();
    }

    fitToMatches() {
      if (!this.mapReady) return;
      const withGeo = this.matches.filter(
        (r) => typeof r.y === 'number' && typeof r.x === 'number'
      );
      if (withGeo.length === 0) return;
      // A single far-away race (Hawaii next to 1,900 European ones) used to
      // drag the fit out to a world view where everything collapses into a
      // few mega clusters. If one region holds the clear majority of the
      // geo races, frame THAT region — the rest stays reachable by zooming
      // out, panning, or the country dropdown.
      let fitSet = withGeo;
      const byRegion = new Map();
      withGeo.forEach((r) => {
        const region = r.r || 'other';
        const list = byRegion.get(region);
        if (list) list.push(r);
        else byRegion.set(region, [r]);
      });
      let dominant = null;
      byRegion.forEach((list) => {
        if (!dominant || list.length > dominant.length) dominant = list;
      });
      // Only region-focus with a meaningful dataset: with a handful of geo
      // races a single-race "region" could win and the map would frame ONE
      // pin at max zoom (seen live while the collection list was incomplete).
      if (dominant && dominant.length >= 8 && dominant.length >= withGeo.length * 0.5) {
        fitSet = dominant;
      }
      const points = fitSet.map((r) => [r.x, r.y]);
      const bounds = points.reduce(
        (acc, point) => acc.extend(point),
        new window.maplibregl.LngLatBounds(points[0], points[0])
      );
      // Auto-framing used the map's hard maxZoom 8 — with a filter active
      // (one race type clustered in a small area) the initial fit landed
      // way too close. Capped by the "Auto-framing zoom limit" setting.
      const fitZoom = parseFloat(this.config.mapFitZoom);
      this.map.fitBounds(bounds, {
        padding: 40,
        maxZoom: Number.isFinite(fitZoom) ? fitZoom : 5,
        duration: 0,
      });
    }

    flyToCountry(code) {
      if (!this.mapReady) return;
      const block = this.collections.find(
        (c) => c.cc === code && c.lat !== null && c.lng !== null
      );
      if (!block) {
        this.fitToMatches();
        return;
      }
      const target = { center: [block.lng, block.lat], zoom: block.zoom || 6 };
      if (this.reducedMotion) {
        this.map.jumpTo(target);
      } else {
        this.map.easeTo(Object.assign({ duration: 700 }, target));
      }
    }

    /* ---------- debug (?rf_debug=1 or theme editor) ---------- */

    get debugEnabled() {
      return /[?&]rf_debug=1(&|$)/.test(window.location.search);
    }

    get designMode() {
      return Boolean(window.Shopify && window.Shopify.designMode);
    }

    renderDebug() {
      if (!this.loadComplete || !this.els.debug) return;
      if (!this.debugEnabled && !this.designMode) return;
      const missing = this.sortedRecords.filter(
        (r) => typeof r.y !== 'number' || typeof r.x !== 'number'
      );
      this.els.debug.hidden = false;
      this.els.debug.textContent = '';

      const summary = document.createElement('p');
      summary.className = 'rf__debug-summary';
      summary.textContent = fmt(this.strings.missing_geo, {
        count: this.numberFormat.format(missing.length),
      });
      this.els.debug.append(summary);

      if (this.debugEnabled && missing.length > 0) {
        const list = document.createElement('ul');
        list.className = 'rf__debug-list';
        missing.forEach((record) => {
          const item = document.createElement('li');
          item.textContent = record.h + ' — ' + record.t;
          list.append(item);
        });
        this.els.debug.append(list);
      }
      if (this.debugEnabled) {
        const stats = {};
        this.collectionStats.forEach((value, handle) => {
          stats[handle] = value;
        });
        // eslint-disable-next-line no-console
        console.log(
          '[race-finder] ' + this.collections.length + ' collections (source: ' +
            (this.config.collectionsSource || 'blocks') + '):',
          stats,
          'missing geo:',
          missing
        );
      }
    }

    /* ---------- mobile filter sheet ---------- */

    openSheet() {
      this.root.classList.add('rf--filters-open');
      if (this.els.backdrop) this.els.backdrop.hidden = false;
      if (this.els.openFilters) this.els.openFilters.setAttribute('aria-expanded', 'true');
      if (this.els.closeFilters) this.els.closeFilters.focus();
      this.startLoad();
    }

    closeSheet() {
      const wasOpen = this.root.classList.contains('rf--filters-open');
      this.root.classList.remove('rf--filters-open');
      if (this.els.backdrop) this.els.backdrop.hidden = true;
      if (this.els.openFilters) {
        this.els.openFilters.setAttribute('aria-expanded', 'false');
        if (wasOpen) this.els.openFilters.focus();
      }
    }
  }

  /* ---------- bootstrapping (incl. theme editor) ---------- */

  function init(root) {
    if (!root || instances.has(root)) return;
    try {
      instances.set(root, new RaceFinder(root));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[race-finder] init failed:', e);
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-rf-root]').forEach(init);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-rf-root]').forEach((root) => {
      const instance = instances.get(root);
      if (instance) {
        instance.destroy();
        instances.delete(root);
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
})();
